import $ from 'jquery';
import {DebuggableObject} from './DebuggableObject.js';
import {red} from './Colors.js';
/*!
* original version was :
*
* https://github.com/adampietrasiak/jquery.initialize
*
* Copyright (c) 2015-2016 Adam Pietrasiak
* Released under the MIT license
* https://github.com/timpler/jquery.initialize/blob/master/LICENSE
*
* This is based on MutationObserver
* https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
*/

/**
 * polyfill for IE11
 */
if (!Element.prototype.matches) {
    Element.prototype.matches = Element.prototype.msMatchesSelector;
}

/*!
 * This is a es6 module for the above version
 * depends on jquery
 */
'use strict';

class MutationSelectorObserver extends DebuggableObject {
    combinators = [' ', '>', '+', '~']; // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Selectors#Combinators
    fraternisers = ['+', '~']; // These combinators involve siblings.
    complexTypes = ['ATTR', 'PSEUDO', 'ID', 'CLASS']; // These selectors are based upon attributes.

    constructor(selector, callback, options) {
        super();

        this.selector = selector.trim();
        this.callback = callback;
        this.options = options;

        this.grok();

        if (this.isComplex) {
            this.log(red, 'complex selector used: ', selector);
        }
    }

    // Understand what kind of selector the initializer is based upon.
    grok() {
        // Parse the selector.
        this.isCombinatorial = false;
        this.isFraternal = false;
        this.isComplex = false;
        let token = $.find.tokenize(this.selector);
        for (let i = 0; i < token.length; i++) {
            for (let j = 0; j < token[i].length; j++) {
                if (this.combinators.indexOf(token[i][j].type) != -1) {
                    this.isCombinatorial = true;
                } // This selector uses combinators.

                if (this.fraternisers.indexOf(token[i][j].type) != -1) {
                    this.isFraternal = true;
                } // This selector uses sibling combinators.

                if (this.complexTypes.indexOf(token[i][j].type) != -1) {
                    this.isComplex = true;
                } // This selector is based on attributes.
            }
        }
    }
};

class Initializer extends DebuggableObject {

    // List of MutationSelectorObservers.
    msobservers = [];
    defaults = {
        target: document.documentElement, // Defaults to observe the entire document.
        observer: null // MutationObserverInit: Defaults to internal configuration if not provided.
    };

    constructor() {
        super();
    }

    add(selector, callback, options) {
        this.log('adding', selector);
        this.options = $.extend({}, this.defaults, options);

        // Wrap the callback so that we can ensure that it is only
        // called once per element.
        let seen = [],
            that = this;
        let callbackOnce = function () {
            if (seen.indexOf(this) == -1) {
                seen.push(this);
                that.log('calling', callback);
                $(this).each(callback);
            }
        };

        // See if the selector matches any elements already on the page.
        $(this.options.target).find(selector).each(callbackOnce);

        // Then, add it to the list of selector observers.
        let msobserver = new MutationSelectorObserver(selector, callbackOnce, this.options);
        this.msobservers.push(msobserver);
        return this.getMutationObserver(msobserver);
    }

    getMutationObserver(msobserver) {
        // The MutationObserver watches for when new elements are added to the DOM.
        let observer = new MutationObserver((mutations) => {
            let matches = [];

            mutations.forEach(mutation => {
                // If this is an attributes mutation, then the target is the node upon which the mutation occurred.
                if (mutation.type == 'attributes') {
                    // Check if the mutated node matches.
                    if (mutation.target.matches(msobserver.selector)) {
                        matches.push(mutation.target);
                    }

                    // If the selector is fraternal, query siblings of the mutated node for matches.
                    if (msobserver.isFraternal) {
                        matches.push.apply(matches, mutation.target.parentElement.querySelectorAll(msobserver.selector));
                    } else {
                        matches.push.apply(matches, mutation.target.querySelectorAll(msobserver.selector));
                    }
                }

                // If this is an childList mutation, then inspect added nodes.
                if (mutation.type == 'childList') {
                    // Search added nodes for matching selectors.
                    mutation.addedNodes.foreach(addedNode => {
                        if (!(addedNode instanceof Element)) return;

                        // Check if the added node matches the selector
                        if (addedNode.matches(msobserver.selector)) {
                            matches.push(addedNode);
                        }

                        // If the selector is fraternal, query siblings for matches.
                        if (msobserver.isFraternal) {
                            matches.push.apply(matches, addedNode.parentElement.querySelectorAll(msobserver.selector));
                        } else {
                            matches.push.apply(matches, addedNode.querySelectorAll(msobserver.selector));
                        }
                    });
                }
            });

            // For each match, call the callback using jQuery.each() to initialize the element (once only.)
            matches.forEach(match => {
                $(match).each(msobserver.callback);
            });
        });

        // Observe the target element.
        let defaultObeserverOpts = {childList: true, subtree: true, attributes: msobserver.isComplex};
        observer.observe(this.options.target, this.options.observer || defaultObeserverOpts);

        return observer;
    }
}

export const initializer = new Initializer();